import React, { useState } from 'react';
import { Copy, Check, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { PageContainer } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';

const SnippetGuide = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSections(newExpanded);
  };

  const CodeBlock = ({ code, id, language = 'html' }: { code: string; id: string; language?: string }) => (
    <div className="relative mt-2">
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
      >
        {copiedCode === id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-300" />}
      </button>
    </div>
  );

  const WarningBox = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mt-4">
      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
      <div className="text-yellow-800 text-sm">{children}</div>
    </div>
  );

  const InfoBox = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4">
      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
      <div className="text-blue-800 text-sm">{children}</div>
    </div>
  );

  const TroubleshootingItem = ({ symptom, solution, index }: { symptom: string; solution: string; index: number }) => {
    const isExpanded = expandedSections.has(index);
    return (
      <div className="border border-gray-200 rounded-lg mb-3">
        <button
          onClick={() => toggleSection(index)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <span className="font-medium text-gray-800">{symptom}</span>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        {isExpanded && (
          <div className="p-4 bg-white border-t border-gray-200">
            <p className="text-gray-700 text-sm">
              <span className="font-semibold">해결:</span> {solution}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <PageTitle title="스니펫 가이드" />
      <PageContainer maxWidth="full">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">JavaScript 스니펫 가이드</h1>
        <p className="text-gray-600">
          웹페이지에 간단한 스크립트 태그를 추가하여, 페이지 내의 모든 이미지에 자동으로 AI 기반의 ALT 텍스트를 생성하는 JavaScript 스니펫 사용 가이드
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-8">
        {/* 1. 개요 */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. 개요</h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">1.1 JavaScript 스니펫이란?</h3>
            <p className="text-gray-700 leading-relaxed">
              JavaScript 스니펫(Snippet)은 웹페이지에 간단한 스크립트 태그를 추가하여 특정 기능을 실행할 수 있도록 제공되는 작은 코드 조각입니다. 
              별도의 개발 없이 HTML 파일에 몇 줄의 코드만 추가하면 원하는 기능을 즉시 사용할 수 있습니다.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">1.2 alttext-auto.js 소개</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <code className="bg-gray-100 px-2 py-1 rounded text-sm">alttext-auto.js</code>는 웹페이지에 간단한 스크립트 태그를 추가하여, 
              페이지 내의 모든 이미지에 자동으로 AI 기반의 ALT 텍스트를 생성해주는 JavaScript 스니펫입니다.
            </p>
            
            <h4 className="font-medium text-gray-800 mb-2">주요 기능</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>페이지 로드 시 자동으로 이미지 분석 및 ALT 텍스트 생성</li>
              <li>기존 ALT 속성이 있는 이미지도 새로운 AI ALT로 교체</li>
              <li>캐싱 기능으로 동일 이미지 재생성 방지</li>
              <li>동적으로 추가된 이미지도 자동 처리 (MutationObserver)</li>
              <li>커스텀 컨텍스트 프로필 지원</li>
              <li>원래 ALT 텍스트 수집 및 전달</li>
            </ul>
          </div>
        </section>

        {/* 2. 설치 방법 */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. 설치 방법</h2>
          
          <h3 className="text-lg font-medium text-gray-800 mb-2">2.1 기본 설치</h3>
          <p className="text-gray-700 mb-4">웹페이지의 <code className="bg-gray-100 px-2 py-1 rounded text-sm">&lt;head&gt;</code> 태그 내에 다음 스크립트를 추가하세요:</p>
          
          <CodeBlock
            id="basic-install"
            code={`<script 
  src="https://alt.aigeneration-tech.com/alttext-auto.js"
  data-api-key="YOUR_API_KEY"
  data-auto-apply="true"
  data-use-context-profile="true">
</script>`}
          />
        </section>

        {/* 3. 설정 옵션 */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. 설정 옵션</h2>
          
          <h3 className="text-lg font-medium text-gray-800 mb-3">필수 속성</h3>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">속성</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">설명</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">예시</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded">src</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700">스크립트 파일 경로</td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">https://alt.aigeneration-tech.com/alttext-auto.js</code></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded">data-api-key</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700">API 키 (필수)</td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">208c8aed-9943-4302-99c2-5fcc1d09be79</code></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-lg font-medium text-gray-800 mb-3 mt-6">선택적 속성</h3>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">속성</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">설명</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">기본값</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">예시</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded">data-auto-apply</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700">ALT 텍스트 자동 적용 여부</td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">true</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">true, false</code></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded">data-use-context-profile</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700">페이지 컨텍스트 분석으로 더 정확한 ALT 생성</td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">true</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">true, false</code></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded">data-delay</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700">페이지 로드 후 대기 시간 (ms)</td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">1000</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">2000</code></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded">data-timeout</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700">API 호출 타임아웃 (ms)</td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">10000</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">15000</code></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded">data-max-retries</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700">실패 시 최대 재시도 횟수</td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">2</code></td>
                  <td className="px-4 py-3 text-sm text-gray-700"><code className="bg-gray-100 px-2 py-1 rounded text-xs">3</code></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. 사용 예시 */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. 사용 예시</h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">4.1 기본 사용</h3>
            <CodeBlock
              id="basic-usage"
              code={`<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <title>내 웹사이트</title>
  <script 
    src="https://alt.aigeneration-tech.com/alttext-auto.js"
    data-api-key="YOUR_API_KEY"
    data-auto-apply="true"
    data-use-context-profile="true">
  </script>
</head>
<body>
  <img src="https://example.com/image1.jpg" alt="">
  <img src="https://example.com/image2.jpg" alt="">
</body>
</html>`}
            />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">4.2 컨텍스트 프로필 비활성화</h3>
            <CodeBlock
              id="context-profile-off"
              code={`<script 
  src="https://alt.aigeneration-tech.com/alttext-auto.js"
  data-api-key="YOUR_API_KEY"
  data-auto-apply="true"
  data-use-context-profile="false">
</script>`}
            />
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">4.3 특정 이미지 건너뛰기</h3>
            <CodeBlock
              id="skip-image"
              code={`<!-- 이 이미지는 처리되지 않음 -->
<img src="image.jpg" data-alttext-auto-skip="true">`}
            />
          </div>
        </section>

        {/* 5. 브라우저 콘솔 로그 */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. 브라우저 콘솔 로그</h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">5.1 초기화</h3>
            <CodeBlock
              id="log-init"
              code={`[AI ALT Text] 초기화 완료`}
              language="text"
            />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">5.2 처리 시작</h3>
            <CodeBlock
              id="log-start"
              code={`[AI ALT Text] 이미지 분석 시작...
[AI ALT Text] 20개의 이미지 발견`}
              language="text"
            />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">5.3 각 이미지 처리</h3>
            <CodeBlock
              id="log-process"
              code={`[AI ALT Text] ALT 적용됨: https://images.unsplash.com/... -> [생성된 alt 텍스트]`}
              language="text"
            />
            <p className="text-gray-600 text-sm mt-2">또는 캐시 히트 시:</p>
            <CodeBlock
              id="log-cache"
              code={`[AI ALT Text] 캐시된 ALT 텍스트 재사용: https://images.unsplash.com/...`}
              language="text"
            />
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">5.4 처리 완료</h3>
            <CodeBlock
              id="log-complete"
              code={`[AI ALT Text] 처리 완료 - 성공: 20, 실패: 0`}
              language="text"
            />
          </div>
        </section>

        {/* 6. 주의사항 */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. 주의사항</h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">6.1 API 키와 도메인 설정</h3>
            <WarningBox>
              <ul className="space-y-1">
                <li>• API 키는 반드시 설정해야 합니다. 설정하지 않으면 오류가 발생합니다</li>
                <li>• 도메인 바인딩은 보안을 위해 반드시 설정해야 합니다</li>
                <li>• 도메인 바인딩을 설정하면 허용된 도메인에서만 API 키를 사용할 수 있어 보안이 강화됩니다</li>
                <li>• 관리자 페이지에서 도메인 제한을 설정할 수 있습니다</li>
              </ul>
            </WarningBox>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">6.2 성능</h3>
            <InfoBox>
              <ul className="space-y-1">
                <li>• 이미지가 많은 페이지 (100개 이상)는 처리 시간이 길어질 수 있음</li>
                <li>• 요청 간격 200ms로 설정되어 있어 API 부하 방지</li>
                <li>• 처리 빈도 제한 (최대 10회/분)</li>
              </ul>
            </InfoBox>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-2">6.3 호환성</h3>
            <InfoBox>
              <ul className="space-y-1">
                <li>• 최신 브라우저 지원 (Chrome, Firefox, Safari, Edge)</li>
                <li>• <code className="bg-gray-100 px-1 rounded text-xs">MutationObserver</code> 미지원 브라우저에서는 동적 이미지 처리 불가</li>
                <li>• IE11 미지원</li>
              </ul>
            </InfoBox>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">6.4 설치 후 확인</h3>
            <InfoBox>
              <ul className="space-y-1">
                <li>• 스크립트를 설치한 후 반드시 브라우저 개발자 도구(F12)를 열어 콘솔 로그를 확인하세요</li>
                <li>• <code className="bg-gray-100 px-1 rounded text-xs">[AI ALT Text] 초기화 완료</code> 메시지가 보이면 정상적으로 로드된 것입니다</li>
                <li>• <code className="bg-gray-100 px-1 rounded text-xs">[AI ALT Text] 처리 완료</code> 메시지가 보이면 모든 이미지 처리가 완료된 것입니다</li>
              </ul>
            </InfoBox>
          </div>
        </section>

        {/* 7. 트러블슈팅 */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. 트러블슈팅</h2>
          
          <TroubleshootingItem
            index={0}
            symptom="7.1 스크립트가 로드되지 않음"
            solution="스크립트 경로 확인, 네트워크 탭에서 로드 실패 확인, CORS 설정 확인"
          />
          
          <TroubleshootingItem
            index={1}
            symptom="7.2 API 키 오류"
            solution="data-api-key 속성 확인, API 키 유효성 확인"
          />
          
          <TroubleshootingItem
            index={2}
            symptom="7.3 ALT 텍스트가 생성되지 않음"
            solution="data-auto-apply가 true인지 확인, 이미지에 data-alttext-auto-skip='true'가 없는지 확인, 네트워크 탭에서 API 호출 성공 확인"
          />
          
          <TroubleshootingItem
            index={3}
            symptom="7.4 동적 이미지가 처리되지 않음"
            solution="MutationObserver 지원 브라우저인지 확인, 처리 빈도 제한 (10회/분) 확인"
          />
        </section>
      </div>
    </PageContainer>
    </>
  );
};

export default SnippetGuide;
